/**
 * The sheet's header: the record's key and a kicker on one line with the
 * tools (Ask, More, Expand, Close), the title under it, and a row of meta
 * chips. Expand is a labelled pill, not a glyph: the full width is where a
 * live run is watched. Ask shows on a phone only, where the sheet covers the
 * Ask bar; on a phone Expand hides, since the sheet already fills the screen.
 * The tools drive the app sheet unless the caller hands its own.
 */
import type { ReactNode } from "react";
import { useSheet } from "../shell/useSheet.js";
import { CloseIcon, ExpandIcon, MoreIcon, ShrinkIcon } from "./icons.js";
import { MoreMenu } from "./MoreMenu.js";
import { Btn, cx } from "./primitives.js";

export interface RecordHeadProps {
  /** The record's key in mono. */
  recordKey?: ReactNode;
  /** What sits after the key: the stage button, a pill. */
  kick?: ReactNode;
  /** The More menu's rows (MenuItem / MenuSeparator); omitted, no More button. */
  more?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  /** Hide the Expand pill (a record with nothing to widen). */
  noExpand?: boolean;
  /** Open the Ask dock on this record; omitted, no Ask button. */
  onAsk?: () => void;
  onClose?: () => void;
}

export function RecordHead({ recordKey, kick, more, title, meta, noExpand, onAsk, onClose }: RecordHeadProps) {
  const sheet = useSheet();
  const close = onClose ?? sheet.close;
  return (
    <div className="shd">
      <div className="kicker">
        {recordKey != null && <span className="key">{recordKey}</span>}
        {kick}
        <span className="tools">
          {onAsk && (
            <Btn size="sm" className="askbtn" aria-label="Ask YAGNI about this record" title="Ask YAGNI about this record" onClick={onAsk}>
              Ask
            </Btn>
          )}
          {more != null && (
            <MoreMenu
              label="More"
              trigger={({ open, toggle }) => (
                <Btn icon aria-label="More" aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
                  <MoreIcon />
                </Btn>
              )}
            >
              {more}
            </MoreMenu>
          )}
          {!noExpand && (
            <Btn
              size="sm"
              className={cx("wide", sheet.wide && "shrink")}
              title={sheet.wide ? "Back to the sheet" : "Open as the full page"}
              onClick={sheet.toggleWide}
            >
              {sheet.wide ? <ShrinkIcon /> : <ExpandIcon />}
              {sheet.wide ? "Shrink" : "Expand"}
            </Btn>
          )}
          <Btn icon aria-label="Close" onClick={close}>
            <CloseIcon />
          </Btn>
        </span>
      </div>
      <h2>{title}</h2>
      {meta != null && <div className="meta">{meta}</div>}
    </div>
  );
}
